import React from 'react';

import { Body, Heading } from '@depop/web-thrift-ui/Typography';
import clsx from 'clsx';

import styles from './styles.module.css';

import { ExtendedLink } from '@/components/ExtendedLink';
import {
  ActivityTrackerEventType,
  moduleOriginParamName,
  NavigationComponentTypes,
} from '@/modules/activityTracker/constants';
import { useActivityTracker } from '@/modules/activityTracker/useActivityTracker';
import { isNotNull } from '@/modules/cms/helpers/isNotNull';
import { TrackingType } from '@/modules/home/types.ts';
import { type CategoryList } from '@/modules/meganav/types';
import { appendParams } from '@/modules/routing/appendParams/appendParams.ts';
import { useCurrentUser } from '@/modules/user/useCurrentUser';

type Props = {
  config: CategoryList;
  className?: string;
};

export function NavigationCategoryList({ className, config }: Props) {
  const [, currentUser] = useCurrentUser();
  const { sendActivityTrackerEvent } = useActivityTracker({
    userId: currentUser?.id,
  });

  function sendTracking(params: {
    href: string;
    label: string;
    componentType: string;
  }) {
    sendActivityTrackerEvent(ActivityTrackerEventType.NAVBAR_CLICK_ACTION, {
      ...params,
      schemaVersion: '1.0',
    });
  }

  const {
    itemsCollection: { items = [] },
  } = config;

  return (
    <div className={clsx(styles.categoryListContainer, className)}>
      <Heading variant="sm" className={styles.categoryListTitle}>
        {config.title}
      </Heading>
      <ul
        className={clsx({
          [styles.categoryListItemsContainer__largeList]: items.length > 8,
          [styles.categoryListItemsContainer__smallList]: items.length <= 8,
        })}
      >
        {items.filter(isNotNull).map(({ label, slug, colourOverride }) => (
          <li className={styles.categoryListitem} key={label}>
            <ExtendedLink
              className={styles.categoryListLink}
              onClick={() =>
                sendTracking({
                  href: slug || '',
                  label,
                  componentType: NavigationComponentTypes.TextLink,
                })
              }
              href={appendParams(slug || '', [
                moduleOriginParamName,
                TrackingType.meganav,
              ])}
            >
              <Body
                variant="sm-regular"
                style={colourOverride ? { color: colourOverride } : {}}
              >
                {label}
              </Body>
            </ExtendedLink>
          </li>
        ))}
      </ul>
      {config.footer && (
        <ExtendedLink
          className={styles.categoryListLink}
          onClick={() => {
            sendTracking({
              href: config.footer?.slug || '',
              label: config.footer?.label || '',
              componentType: NavigationComponentTypes.TextLinkFooter,
            });
          }}
          href={config.footer.slug || ''}
        >
          <Body variant="sm-bold" className={styles.categoryListFooter}>
            {config.footer.label}
          </Body>
        </ExtendedLink>
      )}
    </div>
  );
}
